<template>
  <Dialog
    :visible="visible"
    :header="title"
    :modal="true"
    :closable="true"
    @update:visible="$emit('close')"
    :style="{ width: '600px' }"
    class="tvshow-details-modal"
  >
    <div class="tvshow-details">
      <!-- Start Date -->
      <div v-if="metadata.start_date" class="detail-row">
        <label class="detail-label">
          <Calendar :size="16" />
          <span>Start Date</span>
        </label>
        <span class="detail-value">{{ formatStartDate(metadata.start_date) }}</span>
      </div>

      <!-- Seasons -->
      <div v-if="metadata.seasons" class="detail-row">
        <label class="detail-label">
          <Film :size="16" />
          <span>Seasons</span>
        </label>
        <span class="detail-value">{{ metadata.seasons.content }} {{ metadata.seasons.content === 1 ? 'season' : 'seasons' }}</span>
      </div>

      <!-- Genres -->
      <div v-if="metadata.genres && metadata.genres.content && metadata.genres.content.length > 0" class="detail-row">
        <label class="detail-label">
          <Film :size="16" />
          <span>Genres</span>
        </label>
        <span class="detail-value">{{ formatGenres(metadata.genres.content) }}</span>
      </div>

      <!-- IMDb Rating -->
      <div v-if="metadata.imdb_rating" class="detail-row">
        <label class="detail-label">
          <img src="/imdb-logo-black.svg" alt="IMDb" class="imdb-logo" />
          <span>IMDb Rating</span>
        </label>
        <span class="detail-value">{{ metadata.imdb_rating.content }}/10</span>
      </div>

      <!-- Rotten Tomatoes Score -->
      <div v-if="metadata.rotten_tomatoes_score" class="detail-row">
        <label class="detail-label">
          <ThumbsUp :size="16" />
          <span>Rotten Tomatoes</span>
        </label>
        <span class="detail-value">{{ metadata.rotten_tomatoes_score.content }}%</span>
      </div>

      <!-- User Rating -->
      <div v-if="metadata.rating" class="detail-row">
        <label class="detail-label">
          <Star :size="16" />
          <span>Rating</span>
        </label>
        <span class="detail-value">{{ formatRating(metadata.rating) }}</span>
      </div>

      <!-- Trailer -->
      <div v-if="metadata.trailer" class="detail-row trailer-section">
        <label class="detail-label">
          <Film :size="16" />
          <span>Trailer</span>
        </label>
        <a 
          :href="metadata.trailer.content" 
          target="_blank" 
          rel="noopener noreferrer"
          class="trailer-link"
        >
          <i class="pi pi-external-link"></i>
          Watch Trailer
        </a>
      </div>

      <!-- External Links -->
      <div v-if="hasExternalLinks" class="detail-row external-links">
        <label class="detail-label">
          <Globe :size="16" />
          <span>External Links</span>
        </label>
        <div class="links-group">
          <a 
            v-if="metadata.imdb_id"
            :href="`https://www.imdb.com/title/${metadata.imdb_id.content}`"
            target="_blank"
            rel="noopener noreferrer"
            class="external-link"
          >
            <img src="/imdb-logo-black.svg" alt="IMDb" class="link-icon" />
            IMDb
          </a>
          <a 
            v-if="metadata.tmdb_id"
            :href="`https://www.themoviedb.org/tv/${metadata.tmdb_id.content}`"
            target="_blank"
            rel="noopener noreferrer"
            class="external-link"
          >
            <Film :size="14" />
            TMDB
          </a>
          <a
            v-if="metadata.wikidata_id"
            :href="`https://www.wikidata.org/wiki/${metadata.wikidata_id.content}`"
            target="_blank"
            rel="noopener noreferrer"
            class="external-link"
          >
            <Globe :size="14" />
            Wikidata
          </a>
        </div>
      </div>

      <!-- Where to watch (lazy chunk; never mounts on public/embed renders — showStreaming stays false there) -->
      <StreamingWhereToWatch v-if="showStreaming && linkId" :link-id="linkId" :metadata="metadata" />
    </div>
  </Dialog>
</template>

<script>
import { computed, defineAsyncComponent } from 'vue'
import Dialog from 'primevue/dialog'
import { Calendar, Film, ThumbsUp, Star, Globe } from 'lucide-vue-next'
import { formatDateLong } from '../utils/metadataFormatters.js'

// Deliberately a LAZY import: StreamingWhereToWatch uses i18n, and this file is part of the
// i18n-free embed bundle closure (tests/embedNoI18n.test.js). The chunk only loads when the
// section actually renders, which never happens on public/embed views (showStreaming=false).
const StreamingWhereToWatch = defineAsyncComponent(() => import('./StreamingWhereToWatch.vue'))

export default {
  name: 'TvShowDetailsModal',
  components: {
    Dialog,
    Calendar,
    Film,
    ThumbsUp,
    Star,
    Globe,
    StreamingWhereToWatch
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      default: 'TV Show Details'
    },
    metadata: {
      type: Object,
      required: true
    },
    linkId: {
      type: [Number, String],
      default: null
    },
    // Fail-closed: only the logged-in app passes true (never public/embed renders).
    showStreaming: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props) {
    const formatStartDate = (dateData) => {
      if (!dateData || !dateData.content) return ''
      
      return formatDateLong(dateData.content)
    }

    const formatRating = (ratingData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      
      // If rating is < 1, show as percentage
      if (rating < 1) {
        return `${Math.round(rating * 100)}%`
      }
      
      return rating.toFixed(1)
    }

    const formatGenres = (genres) => {
      if (!Array.isArray(genres)) return ''
      // Capitalize first letter of each genre and join with commas
      return genres.map(genre => 
        String(genre).charAt(0).toUpperCase() + String(genre).slice(1)
      ).join(', ')
    }

    const hasExternalLinks = computed(() => {
      return Boolean(
        props.metadata.imdb_id?.content ||
        props.metadata.tmdb_id?.content ||
        props.metadata.wikidata_id?.content
      )
    })

    return {
      formatStartDate,
      formatRating,
      formatGenres,
      hasExternalLinks
    }
  }
}
</script>

<style scoped>
.tvshow-details-modal :deep(.p-dialog-header) {
  background: linear-gradient(135deg, #e77403 0%, #d16603 100%);
  color: white;
  padding: 1.5rem;
}

.tvshow-details-modal :deep(.p-dialog-header .p-dialog-title) {
  font-weight: 500;
  font-size: 1.25rem;
}

.tvshow-details-modal :deep(.p-dialog-header .p-dialog-header-icon) {
  color: white;
}

.tvshow-details-modal :deep(.p-dialog-header .p-dialog-header-icon):hover {
  background: rgba(255, 255, 255, 0.2);
}

.tvshow-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 0;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  transition: background 0.2s ease;
}

.detail-row:hover {
  background: var(--bg-tertiary, var(--bg-primary));
}

.detail-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.detail-label svg {
  color: #e77403;
  flex-shrink: 0;
}

.detail-label .imdb-logo {
  height: 16px;
  width: auto;
}

.detail-value {
  color: var(--text-secondary);
  font-size: 0.9rem;
  text-align: right;
}

.trailer-section {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.trailer-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #e77403 0%, #d16603 100%);
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  transition: all 0.2s ease;
  width: 100%;
  justify-content: center;
}

.trailer-link:hover {
  background: linear-gradient(135deg, #d16603 0%, #bb5903 100%);
  box-shadow: 0 4px 12px rgba(231, 116, 3, 0.3);
  transform: translateY(-1px);
}

.external-links {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.links-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
}

.external-link {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: rgba(231, 116, 3, 0.1);
  color: #e77403;
  text-decoration: none;
  border-radius: 6px;
  border: 1px solid rgba(231, 116, 3, 0.2);
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.external-link:hover {
  background: rgba(231, 116, 3, 0.2);
  border-color: rgba(231, 116, 3, 0.4);
  transform: translateY(-1px);
}

.external-link svg {
  color: #e77403;
  flex-shrink: 0;
}

.external-link .link-icon {
  height: 14px;
  width: auto;
}

/* Dark theme: the brand orange #e77403 is low-contrast on dark, so lighten it
   (token-based surfaces flip on their own). */
:root.dark-theme .detail-label svg {
  color: #ff9347;
}

:root.dark-theme .external-link {
  background: rgba(231, 116, 3, 0.15);
  color: #ff9347;
  border-color: rgba(231, 116, 3, 0.3);
}

:root.dark-theme .external-link:hover {
  background: rgba(231, 116, 3, 0.25);
  border-color: rgba(231, 116, 3, 0.5);
}

:root.dark-theme .external-link svg {
  color: #ff9347;
}

/* The IMDb wordmark is a pure-black <img> asset no color rule can reach —
   invert it to white on dark. */
:root.dark-theme .detail-label .imdb-logo,
:root.dark-theme .external-link .link-icon {
  filter: invert(1);
}
</style>
